import { textoItens } from '../CampoItens';
import { textoNd } from '../CampoNdVinculada';
import { useMemo } from 'react';
import { XMarkIcon, PrinterIcon } from '@heroicons/react/24/outline';
import type { Card, Etapa, CampoConfig } from '../../types';

interface ModalResumoProps {
  cardPai: Card;
  etapa: Etapa;
  todasEtapas?: Etapa[]; // Todas as etapas do fluxo para mostrar histórico completo
  onClose: () => void;
}

const ModalResumo = ({ cardPai, etapa, todasEtapas = [], onClose }: ModalResumoProps) => {
  const filhos = cardPai.cardsFilhos || [];
  const totalFilhos = filhos.length;
  // Helper: converter valor brasileiro (53,98) ou americano (53.98) para número
  const parseValorBrasileiro = (valor: any): number => {
    if (valor === undefined || valor === null || valor === '') return 0;
    if (typeof valor === 'number') return isNaN(valor) ? 0 : valor;
    const valorStr = String(valor).trim();
    // Se tem vírgula, é formato brasileiro: 1.234,56 -> remove pontos e troca vírgula por ponto
    if (valorStr.includes(',')) {
      const valorConvertido = valorStr.replace(/\./g, '').replace(',', '.');
      const numero = Number(valorConvertido);
      return isNaN(numero) ? 0 : numero;
    }
    // Se só tem ponto (ou nenhum), já está em formato válido: 1234.56
    const numero = Number(valorStr);
    return isNaN(numero) ? 0 : numero;
  };
  // Helper: buscar valor de um campo no card (campos atuais + etapaHistory)
  const getValorCampo = (targetCard: Card, campoId: string) => {
    if (targetCard?.campos?.[campoId] !== undefined && targetCard.campos[campoId] !== '') {
      return targetCard.campos[campoId];
    }
    if (targetCard?.etapaHistory && Array.isArray(targetCard.etapaHistory)) {
      for (let i = targetCard.etapaHistory.length - 1; i >= 0; i--) {
        const histEntry = targetCard.etapaHistory[i];
        if (histEntry?.camposSnapshot?.[campoId] !== undefined && histEntry.camposSnapshot[campoId] !== '') {
          return histEntry.camposSnapshot[campoId];
        }
      }
    }
    return undefined;
  };

  // Encontrar o primeiro campo do tipo 'valor' em TODAS as etapas
  const campoValor = useMemo(() => {
    const campoLocal = etapa.campos?.find(c => c.tipo === 'valor');
    if (campoLocal) return campoLocal;
    for (const e of todasEtapas) {
      const campo = e.campos?.find(c => c.tipo === 'valor');
      if (campo) return campo;
    }
    return null;
  }, [etapa.campos, todasEtapas]);

  // Calcular valor total (soma do campo valor do pai + filhos)
  const valorTotalCalculado = useMemo(() => {
    if (!campoValor) return 0;
    // Incluir valor do próprio card pai
    let soma = parseValorBrasileiro(getValorCampo(cardPai, campoValor.id));
    // Somar valores dos filhos
    filhos.forEach(filho => {
      soma += parseValorBrasileiro(getValorCampo(filho, campoValor.id));
    });
    return soma;
  }, [campoValor, cardPai, filhos]);

  const valorTotalExibido = (cardPai.valorTotal != null && !isNaN(cardPai.valorTotal)) 
    ? cardPai.valorTotal 
    : valorTotalCalculado;

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const handleImprimir = () => {
    window.print();
  };

  // Obter todas as etapas que o card passou (baseado nos campos preenchidos)
  const getEtapasComDados = (card: Card): Etapa[] => {
    const etapasIds = new Set<string>();
    
    // Adicionar etapa atual
    if (card.etapaId) {
      etapasIds.add(String(card.etapaId));
    }
    
    // Verificar campos para encontrar etapas com dados
    if (card.campos) {
      Object.keys(card.campos).forEach(campoId => {
        const valor = card.campos?.[campoId];
        if (valor !== undefined && valor !== null && valor !== '') {
          // Procurar em qual etapa esse campo pertence
          todasEtapas.forEach(et => {
            if (et.campos?.some(c => c.id === campoId)) {
              etapasIds.add(String(et.id));
            }
          });
        }
      });
    }
    
    // Retornar etapas ordenadas pelo array original (ordem do fluxo)
    return todasEtapas.filter(et => etapasIds.has(String(et.id)));
  };

  const renderValorCampo = (campo: CampoConfig, valor: any): React.ReactNode => {
    if (valor === undefined || valor === null || valor === '') {
      return '—';
    }

    switch (campo.tipo) {
      case 'texto':
      case 'texto_longo':
        return String(valor);
      
      case 'data':
      case 'data_vencimento':
        try {
          const data = new Date(valor);
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          const dataVenc = new Date(data);
          dataVenc.setHours(0, 0, 0, 0);
          const vencido = dataVenc < hoje;
          
          const dataFormatada = data.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric'
          });
          
          return vencido ? `${dataFormatada} (VENCIDO)` : dataFormatada;
        } catch {
          return String(valor);
        }
      
      case 'lista':
        return String(valor);
      
      case 'itens':
        return textoItens(valor) || '—';

      case 'nd_vinculada':
        return textoNd(valor) || '—';

      case 'parcelas':
        if (Array.isArray(valor) && valor.length > 0) {
          const pagas = valor.filter((p: any) => p?.paga).length;
          return `${pagas}/${valor.length} pagas — `
            + valor.map((p: any) => `${p?.nome ?? ''}${p?.paga ? ' ✓' : ''}`).join(', ');
        }
        return '—';

      case 'checkbox':
      case 'checkbox_single':
      case 'checkbox_multi':
        if (Array.isArray(valor)) {
          const selecionados = valor.filter(Boolean);
          return selecionados.length > 0 ? selecionados.join(', ') : '—';
        }
        return valor === true ? 'Sim' : valor === false ? 'Não' : String(valor);
      
      case 'valor':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(parseValorBrasileiro(valor));
      
      case 'anexo': {
        const arquivos = Array.isArray(valor) ? valor : valor ? [valor] : [];
        if (arquivos.length === 0) return '—';
        return (
          <span className="space-x-2">
            {arquivos.map((arquivo: any, idx: number) => {
              const nome = typeof arquivo === 'string' ? arquivo : (arquivo?.name || arquivo?.fileName || arquivo?.originalName || 'Arquivo');
              const url = typeof arquivo === 'object' ? (arquivo?.webViewLink || arquivo?.webContentLink || arquivo?.url || arquivo?.dataUrl) : (typeof arquivo === 'string' && arquivo.startsWith('http') ? arquivo : null);
              return url ? (
                <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  📎 {nome}
                </a>
              ) : (
                <span key={idx}>📎 {nome}</span>
              );
            })}
          </span>
        );
      }
      
      case 'telefone':
        return `📱 ${valor}`;
      
      default:
        return String(valor);
    }
  };

  // Renderiza um card com TODAS as etapas que ele passou
  const renderCardCompleto = (card: Card, titulo: string, index?: number) => {
    const etapasDoCard = todasEtapas.length > 0 ? getEtapasComDados(card) : [etapa];
    
    return (
      <div className="mb-3 break-inside-avoid">
        {/* Cabeçalho do Card */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-3 py-2 rounded-t-lg print:bg-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold">{titulo}</h3>
              <p className="text-xs text-purple-100">{card.titulo}</p>
            </div>
            {index !== undefined && (
              <div className="text-lg font-bold opacity-50">#{index + 1}</div>
            )}
          </div>
        </div>

        {/* Campos agrupados por etapa */}
        <div className="border border-purple-200 rounded-b-lg bg-white print:border-gray-300 text-sm">
          {etapasDoCard.map((etapaItem, etapaIdx) => {
            // Filtrar campos que têm valor preenchido
            const camposComValor = etapaItem.campos?.filter(campo => {
              const valor = card.campos?.[campo.id];
              return valor !== undefined && valor !== null && valor !== '';
            }) || [];
            
            if (camposComValor.length === 0) return null;
            
            return (
              <div key={etapaItem.id} className={etapaIdx > 0 ? 'border-t border-gray-200' : ''}>
                {/* Nome da etapa */}
                <div className="bg-gray-50 px-3 py-1 border-b border-gray-100">
                  <span className="text-xs font-semibold text-gray-600">
                    📍 {etapaItem.nome}
                  </span>
                </div>
                
                {/* Campos da etapa em grid compacto */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 p-2">
                  {camposComValor.map((campo) => {
                    const valor = card.campos?.[campo.id];
                    const valorFormatado = renderValorCampo(campo, valor);
                    
                    return (
                      <div key={campo.id} className="text-xs py-0.5">
                        <span className="text-gray-500 text-[10px] uppercase">{campo.nome}: </span>
                        <span className="text-gray-900 font-medium">{valorFormatado}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Estilos para impressão */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-container, .print-container * { visibility: visible; }
          .print-container { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[75vh] overflow-hidden flex flex-col">
          {/* Header compacto */}
          <div className="no-print bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold">Resumo Completo</h2>
              <p className="text-purple-100 text-xs">
                Card Pai + {totalFilhos} {totalFilhos === 1 ? 'filho' : 'filhos'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleImprimir}
                className="flex items-center gap-1 px-2 py-1 bg-white/20 hover:bg-white/30 rounded text-xs"
                title="Imprimir"
              >
                <PrinterIcon className="w-4 h-4" />
                Imprimir
              </button>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded"
                title="Fechar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content com scroll */}
          <div className="flex-1 overflow-y-auto p-3 print-container">
            {/* Card Pai */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
                <span className="text-xs font-bold text-purple-700">CARD PAI</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent"></div>
              </div>
              {renderCardCompleto(cardPai, '🎴 Card Pai')}
            </div>

            {/* Valor Total */}
            {campoValor && (
              <div className="mb-3 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💰</span>
                    <div>
                      <span className="text-xs font-bold text-green-800">VALOR TOTAL</span>
                      <p className="text-[10px] text-green-600">
                        Soma de {campoValor.nome} (pai + {totalFilhos} {totalFilhos === 1 ? 'filho' : 'filhos'})
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-800">
                      {formatarMoeda(valorTotalExibido)}
                    </div>
                    {cardPai.valorTotal != null && cardPai.valorTotal !== valorTotalCalculado && (
                      <div className="text-[10px] text-green-600">
                        Calculado: {formatarMoeda(valorTotalCalculado)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Cards Filhos */}
            {totalFilhos > 0 && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-indigo-400 to-transparent"></div>
                  <span className="text-xs font-bold text-indigo-700">
                    CARDS FILHOS ({totalFilhos})
                  </span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-indigo-400 to-transparent"></div>
                </div>
                
                {filhos.map((filho, index) => (
                  <div key={filho.id}>
                    {renderCardCompleto(filho, `→ Filho ${index + 1}`, index)}
                  </div>
                ))}
              </>
            )}

            {totalFilhos === 0 && (
              <div className="text-center py-4 text-gray-500 text-sm">
                Este card pai ainda não possui filhos
              </div>
            )}
          </div>

          {/* Footer compacto */}
          <div className="no-print bg-gray-100 border-t border-gray-200 px-3 py-2 flex justify-between items-center">
            <div className="text-xs text-gray-600">
              {todasEtapas.length || 1} etapas • {totalFilhos + 1} cards
            </div>
            <button
              onClick={onClose}
              className="px-3 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded hover:from-purple-700 hover:to-indigo-700 text-sm font-medium"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalResumo;
